import React from "react";
import { CreditCard } from "lucide-react";
import { planLabel } from "../../actions/billing";
import { statusBadgeClass } from "./statusBadge";

/**
 * The card the subscription is charged to.
 *
 * GET /billing/subscription carries `paymentMethod`, and its `card` is the
 * part a person recognises: the last four digits and the expiry. It can be
 * null - a plan bought before the card details were recorded - in which case
 * there is still a subscription, just nothing to describe it with.
 */
export const describeCard = (card) => {
  if (!card?.last4) return "";

  return card.expiry
    ? `Card ending ${card.last4} · expires ${card.expiry}`
    : `Card ending ${card.last4}`;
};

export default function PaymentMethodCard({
  subscription,
  loading = false,
  onChangePlan,
}) {
  const method = subscription?.paymentMethod;
  // The id proves a subscription exists; a free plan has none.
  const subscriptionId =
    subscription?.helcimSubscriptionId || method?.subscriptionId;
  const status = subscription?.status;
  const plan = subscription?.plan;
  const card = describeCard(method?.card);

  if (loading) {
    return (
      <section className="dash-card" aria-busy="true">
        <div className="dash-card-header">
          <h2 className="cz-card-title">Payment method</h2>
        </div>
        <div className="bill-card-row">
          <span className="skel bill-method-skel" />
          <div className="bill-card-text">
            <span className="skel acct-skel--label" />
            <span className="skel acct-skel--desc" />
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="dash-card">
      <div className="dash-card-header">
        <h2 className="cz-card-title">Payment method</h2>
      </div>

      {subscriptionId ? (
        <>
          <div className="bill-card-row">
            <span className="bill-card-badge bill-card-badge--card">
              <CreditCard size={20} strokeWidth={1.9} />
            </span>
            <div className="bill-card-text">
              <p>
                {/* The card when the record has one, and the plain fact of a
                    card on file when it does not. */}
                {card || "Card on file"}
                {(method?.statusLabel || status) && (
                  <span className={statusBadgeClass(status)}>
                    {method?.statusLabel || planLabel(status)}
                  </span>
                )}
              </p>
              {plan && <span>Billed for the {planLabel(plan)} plan</span>}
              {method?.card?.holderName && (
                <span>{method.card.holderName}</span>
              )}
            </div>
          </div>

          <p className="ist-card-subtitle bill-method-note">
            {/* The backend words this, and it is the authority on what
                cancelling does. */}
            {method?.cancelNote ||
              "Renewals are charged to this card. Cancelling stops all future payments; your plan keeps working until the end of the period you have already paid for."}
          </p>
        </>
      ) : (
        <>
          <div className="bill-card-row">
            <span className="bill-card-badge bill-card-badge--card">
              <CreditCard size={20} strokeWidth={1.9} />
            </span>
            <div className="bill-card-text">
              <p>No payment method yet</p>
              <span>
                Your card is taken when you move to a paid plan, and kept for
                renewals.
              </span>
            </div>
          </div>

          {onChangePlan && (
            <button
              type="button"
              className="onboarding__btn onboarding__btn--secondary bill-method-btn"
              onClick={onChangePlan}
            >
              Choose a plan
            </button>
          )}
        </>
      )}
    </section>
  );
}
